/**
 * Design System - Alert Component
 *
 * Contextual feedback messages for typical user actions.
 *
 * @example
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved.
 * </Alert>
 *
 * @example
 * <Alert variant="danger" dismissible onDismiss={handleDismiss}>
 *   An error occurred while processing your request.
 * </Alert>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';

const Alert = ({
  children,
  variant = 'info',
  title,
  icon: customIcon,
  dismissible = false,
  onDismiss,
  className,
  ...props
}) => {
  // Variant styles
  const variantStyles = {
    success: 'bg-success-50 border-success-200 text-success-800',
    warning: 'bg-warning-50 border-warning-200 text-warning-800',
    danger: 'bg-danger-50 border-danger-200 text-danger-800',
    info: 'bg-info-50 border-info-200 text-info-800',
  };

  // Default icons
  const defaultIcons = {
    success: FiCheckCircle,
    warning: FiAlertTriangle,
    danger: FiAlertCircle,
    info: FiInfo,
  };

  // Icon colors
  const iconColors = {
    success: 'text-success-600',
    warning: 'text-warning-600',
    danger: 'text-danger-600',
    info: 'text-info-600',
  };

  const Icon = customIcon || defaultIcons[variant];

  return (
    <div
      role="alert"
      className={cn(
        'relative flex gap-3 p-4 border rounded-lg',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {/* Icon */}
      {Icon && (
        <div className="flex-shrink-0">
          <Icon className={cn('h-5 w-5', iconColors[variant])} aria-hidden="true" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className="text-sm font-semibold mb-1">
            {title}
          </h3>
        )}
        <div className="text-sm">
          {children}
        </div>
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'flex-shrink-0 p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors',
            iconColors[variant]
          )}
          aria-label="Cerrar alerta"
        >
          <FiX className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

Alert.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['success', 'warning', 'danger', 'info']),
  title: PropTypes.string,
  icon: PropTypes.elementType,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
};

export default Alert;
